import React, { useState, useMemo } from 'react';
import { ProjectCard } from './components/ProjectCard';
import { FilterBar } from './components/FilterBar';
import { Header } from './components/Header';
import { projectsData } from './data/projects';

function App() {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Get all unique tags from projects
  const allTags = useMemo(() => {
    const tags = new Set<string>();
    projectsData.forEach(project => {
      project.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
  }, []);

  // Filter projects based on selected tags and search query
  const filteredProjects = useMemo(() => {
    return projectsData.filter(project => {
      const matchesTags = selectedTags.length === 0 || 
        selectedTags.some(tag => project.tags.includes(tag));
      
      const matchesSearch = searchQuery === '' ||
        project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.techStack.some(tech => 
          tech.toLowerCase().includes(searchQuery.toLowerCase())
        );

      return matchesTags && matchesSearch;
    });
  }, [selectedTags, searchQuery]);

  const handleTagToggle = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSelectedTags([]);
    setSearchQuery('');
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Filter Section */}
        <FilterBar
          allTags={allTags}
          selectedTags={selectedTags}
          searchQuery={searchQuery}
          onTagToggle={handleTagToggle}
          onSearchChange={setSearchQuery}
          onClearFilters={clearFilters}
        />

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-xs text-gray-600">
            {filteredProjects.length === projectsData.length
              ? `Showing all ${projectsData.length} projects`
              : `Showing ${filteredProjects.length} of ${projectsData.length} projects`
            }
          </p>
        </div>

        {/* Projects Grid - Wider cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredProjects.map((project, index) => (
            <ProjectCard
              key={project.id}
              project={project}
              index={index}
            />
          ))}
        </div>

        {/* No Results */}
        {filteredProjects.length === 0 && (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <h3 className="text-base font-medium text-gray-900 mb-2">No projects found</h3>
            <p className="text-gray-500 mb-3 text-sm">Try adjusting your filters or search query</p>
            <button
              onClick={clearFilters}
              className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Clear all filters
            </button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="text-center text-gray-500 text-xs">
            <p>Built with React, TypeScript, and Tailwind CSS</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
